var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormControl, FormHelperText, Box, List, ListItem, ListItemText, IconButton, Paper, Typography, } from "@mui/material";
import { ArrowUpward, ArrowDownward, DragIndicator } from "@mui/icons-material";
import { buildFieldValidation } from "../../validators/field-components";
import { getElementValueKey, reorderChoices } from "../../../../utils/helpers";
import CommentArea from "../common/CommentArea";
import { useElementConditions } from "../../context/FormLogicContext";
import Header from "../common/Header";
import { getCommentFieldName, getInitialValueForElement, } from "../../../../utils/forms-functions";
import { useClearInvisibleValue } from "../../hooks/useClearInvisibleValue";
var mapRankingValuesToDisplayItems = function (currentValues, initialOrder) {
    if (currentValues.length !== initialOrder.length ||
        currentValues.length === 0) {
        return initialOrder;
    }
    var lookupMap = new Map(initialOrder.map(function (item) { return [item.value, item]; }));
    var mappedItems = currentValues
        .map(function (value) { return lookupMap.get(value); })
        .filter(function (item) { return item !== undefined; });
    return mappedItems.length === currentValues.length
        ? mappedItems
        : initialOrder;
};
var RankingField = function (_a) {
    var element = _a.element, errors = _a.errors;
    var unregister = useFormContext().unregister;
    var valueKey = getElementValueKey(element);
    var error = errors[valueKey];
    var _b = useElementConditions(element), isVisible = _b.isVisible, isEnabled = _b.isEnabled, isInteractive = _b.isInteractive, fieldRequired = _b.isRequired;
    var validation = useMemo(function () {
        return buildFieldValidation(element, {
            isRequired: fieldRequired,
            readOnly: !isEnabled,
        });
    }, [element, fieldRequired, isEnabled]);
    var valueKeys = useMemo(function () {
        return element.showCommentArea
            ? [valueKey, getCommentFieldName(valueKey)]
            : [valueKey];
    }, [valueKey, element.showCommentArea]);
    useClearInvisibleValue({
        isVisible: isVisible,
        valueKeys: valueKeys,
        unregister: unregister,
    });
    var getInitialOrder = useMemo(function () {
        var choices = (element.choices || []);
        var choicesFormatted = choices.map(function (choice) {
            if (typeof choice === "string") {
                return { value: choice, text: choice };
            }
            return choice;
        });
        if (!element.choicesOrder) {
            return choicesFormatted;
        }
        return reorderChoices(choicesFormatted, element.choicesOrder);
    }, [element.choices, element.choicesOrder]);
    var initialValue = getInitialValueForElement(element);
    var _c = useState(null), draggedIndex = _c[0], setDraggedIndex = _c[1];
    var _d = useState(null), dragOverIndex = _d[0], setDragOverIndex = _d[1];
    var moveItem = function (index, direction, onChange, currentItems) {
        var _a;
        if (direction === "up" && index === 0)
            return;
        if (direction === "down" && index === currentItems.length - 1)
            return;
        var newOrder = __spreadArray([], currentItems, true);
        var targetIndex = direction === "up" ? index - 1 : index + 1;
        _a = [
            newOrder[targetIndex],
            newOrder[index],
        ], newOrder[index] = _a[0], newOrder[targetIndex] = _a[1];
        var newValues = newOrder.map(function (item) { return item.value; });
        onChange(newValues);
    };
    var handleDragStart = function (e, index) {
        if (!isInteractive) {
            e.preventDefault();
            return;
        }
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/html", index.toString());
        setTimeout(function () {
            if (e.currentTarget instanceof HTMLElement) {
                e.currentTarget.style.opacity = "0.5";
            }
        }, 0);
    };
    var handleDragEnd = function (e) {
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = "1";
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };
    var handleDragOver = function (e, index) {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };
    var handleDragLeave = function () {
        setDragOverIndex(null);
    };
    var handleDrop = function (e, dropIndex, onChange, currentItems) {
        e.preventDefault();
        setDragOverIndex(null);
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            return;
        }
        var newOrder = __spreadArray([], currentItems, true);
        var draggedItem = newOrder[draggedIndex];
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(dropIndex, 0, draggedItem);
        var newValues = newOrder.map(function (item) { return item.value; });
        onChange(newValues);
        setDraggedIndex(null);
    };
    return (_jsx(_Fragment, { children: isVisible ? (_jsx(Box, __assign({ className: "component-wrapper" }, { children: _jsxs(FormControl, __assign({ fullWidth: true, error: !!error, sx: { mb: 3 } }, { children: [_jsx(Header, { element: element, error: !!error, isRequired: fieldRequired }), _jsx(Controller, { name: valueKey, defaultValue: initialValue, rules: {
                            validate: function (value) {
                                var _a;
                                var result = validation.safeParse(value);
                                if (!result.success) {
                                    return ((_a = result.error.errors[0]) === null || _a === void 0 ? void 0 : _a.message) || "Valor inválido";
                                }
                                return true;
                            },
                        }, render: function (_a) {
                            var field = _a.field;
                            var currentValues = (field.value || []);
                            var displayItems = mapRankingValuesToDisplayItems(currentValues, getInitialOrder);
                            return (_jsxs(Box, { children: [_jsx(Paper, __assign({ variant: "outlined", sx: { p: 2 } }, { children: _jsx(List, __assign({ dense: true }, { children: displayItems.map(function (item, index) { return (_jsxs(ListItem, __assign({ draggable: !!isInteractive, onDragStart: function (e) { return handleDragStart(e, index); }, onDragOver: function (e) { return handleDragOver(e, index); }, onDragEnd: handleDragEnd, onDragLeave: handleDragLeave, onDrop: function (e) {
                                                    return handleDrop(e, index, field.onChange, displayItems);
                                                }, sx: {
                                                    border: 1,
                                                    borderColor: dragOverIndex === index
                                                        ? "primary.main"
                                                        : draggedIndex === index
                                                            ? "primary.light"
                                                            : "divider",
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    bgcolor: dragOverIndex === index
                                                        ? "action.selected"
                                                        : draggedIndex === index
                                                            ? "action.hover"
                                                            : "background.paper",
                                                    opacity: draggedIndex === index ? 0.5 : 1,
                                                    cursor: !isInteractive ? "default" : "grab",
                                                    transition: "all 0.2s ease-in-out",
                                                    "&:active": {
                                                        cursor: !isInteractive ? "default" : "grabbing",
                                                    },
                                                }, secondaryAction: _jsxs(Box, __assign({ sx: { display: "flex", gap: 0.5 } }, { children: [_jsx(IconButton, __assign({ edge: "end", onClick: function () {
                                                                return moveItem(index, "up", field.onChange, displayItems);
                                                            }, disabled: index === 0 || !isInteractive, size: "small", "aria-label": "Mover para cima" }, { children: _jsx(ArrowUpward, { fontSize: "small" }) })), _jsx(IconButton, __assign({ edge: "end", onClick: function () {
                                                                return moveItem(index, "down", field.onChange, displayItems);
                                                            }, disabled: index === displayItems.length - 1 ||
                                                                !isInteractive, size: "small", "aria-label": "Mover para baixo" }, { children: _jsx(ArrowDownward, { fontSize: "small" }) }))] })) }, { children: [_jsx(DragIndicator, { sx: {
                                                            color: !isInteractive
                                                                ? "action.disabled"
                                                                : "action.active",
                                                            mr: 1,
                                                            cursor: !isInteractive ? "default" : "grab",
                                                        } }), _jsx(ListItemText, { primary: _jsxs(Box, __assign({ sx: {
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1,
                                                            } }, { children: [_jsxs(Typography, __assign({ variant: "body2", sx: {
                                                                        fontWeight: 600,
                                                                        color: "primary.main",
                                                                        minWidth: 24,
                                                                    } }, { children: [index + 1, "."] })), _jsx(Typography, __assign({ variant: "body1" }, { children: item.text }))] })) })] }), "".concat(item.value, "-").concat(index))); }) })) })), error && (_jsx(FormHelperText, __assign({ error: true, sx: { mt: 1 } }, { children: error.message }))), element.showCommentArea && (_jsx(CommentArea, { element: element, disabled: !isInteractive }))] }));
                        } })] })) }))) : null }));
};
export default RankingField;
